import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import propertyData from './properties.json';
import Navbar from './Navbar';
import PropertyCover from './PropertyCover';
import PropertyDetailSection from './PropertyDetailSection';
import FixedBookingButton from './FixedBookingButton';
import TopHeader from './TopHeader';
import Footer from './Footer';
import MediaExplorer from './MediaExplorer';  // Replace PictureCollage with MediaExplorer
import { fetchProperty, mediaPrefix } from './api/apiService';
import { useLanguage } from './LanguageContext';
import { SquareLoader } from 'react-spinners';
import { Helmet } from 'react-helmet';
import { useBreakpointContext } from './App';
import Amenities from './Amenities';
import TabBar from './components/TabBrowser/TabBar';


const PropertyView = () => {
    let { slug } = useParams();
    const propertyId = slug !== undefined ? slug : 'dexters-beach-home';
    const property = propertyData.find(prop => prop.id === propertyId);

    const mainContainerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(null);

    const { currentLanguage } = useLanguage();

    const [incoming, setIncoming] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const breakpoint = useBreakpointContext();
    const isMobile = breakpoint.isMobile;


    const textWithBreaks = incoming?.introduction?.split('\n').map((str, index, array) =>
        index === array.length - 1 ? str : <>
            {str}
            <br />
        </>
    );

    useEffect(() => {
        setIncoming(null);
        setLoading(true);
        setError(null);

        const fetchData = async () => {
            try {
                const propertyData = await fetchProperty({ id: propertyId, language: currentLanguage });  // Fetch the specific page based on slug
                setIncoming(propertyData);
                setLoading(false);
                console.log(propertyData);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };
        fetchData();
    }, [slug, currentLanguage]);

    useEffect(() => {
        const updateContainerWidth = () => {
            if (mainContainerRef.current) {
                setContainerWidth(mainContainerRef.current.clientWidth);
            }
        };

        updateContainerWidth();

        const resizeObserver = new ResizeObserver(updateContainerWidth);
        if (mainContainerRef.current) {
            resizeObserver.observe(mainContainerRef.current);
        }

        window.addEventListener('resize', updateContainerWidth);

        return () => {
            if (mainContainerRef.current) {
                resizeObserver.unobserve(mainContainerRef.current);
            }
            window.removeEventListener('resize', updateContainerWidth);
        };
    }, []);

    if (loading) return (
        <div style={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <SquareLoader
                color="#142E5C"
                size={50}
                aria-label="Loading Spinner"
                data-testid="loader"
            />
        </div>
    );
    if (error) return <div>Error: {error}</div>;

    if (!loading && incoming) {
        const metaImage = mediaPrefix() + incoming?.meta?.image?.url;
        return (
            <>
                <Helmet>
                    <title>{incoming?.meta?.title}</title>
                    <meta name="description" content={incoming?.meta?.description} />
                    {/* Add other meta tags as needed */}
                    <meta property="og:title" content={incoming?.meta?.title} />
                    <meta property="og:description" content={incoming?.meta?.description} />
                    <meta property="og:image" content={metaImage} />
                    {/* Add more open-graph or other social tags as needed */}
                </Helmet>
                <FixedBookingButton property={incoming} propertyId={property.hospitableId} propertyTitle={property.name} breakpoint={breakpoint} />
                <div className="flex flex-col items-center h-auto" style={{
                    opacity: 0,
                    animation: 'fadeIn 1s forwards 0.5s, slideUp 1s forwards 0.5s',
                    marginTop: '57px',
                }}>
                    <PropertyCover property={property} incoming={incoming} />


                    <section style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: '20px',
                        minHeight: '50vh',
                        marginTop: '20px',
                        width: 'auto',
                        marginLeft: isMobile ? '10px' : '40px',
                        marginRight: isMobile ? '10px' : '40px',
                    }}>
                        <div style={{
                            padding: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            textAlign: 'center',
                            fontSize: '1.05em',
                            //borderRadius: '15px',
                            padding: isMobile ? '30px' : '50px',
                            marginBottom: '0px',
                            backgroundColor: 'rgb(60, 60, 60)',
                            color: 'rgb(252, 234, 198)',
                            borderRadius: '15px',
                        }}>
                            <svg width="65" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4.09738 2.163C4.23398 2.163 4.35788 2.2186 4.44758 2.3082C4.53768 2.3984 4.59328 2.5223 4.59328 2.6589C4.59328 2.7958 4.53768 2.9199 4.44808 3.0095L4.43998 3.0169C4.35098 3.1022 4.23018 3.1547 4.09738 3.1547C3.96048 3.1547 3.83638 3.0991 3.74678 3.0095L3.73938 3.0014C3.65408 2.9124 3.60158 2.7915 3.60158 2.6589C3.60158 2.5223 3.65718 2.3984 3.74678 2.3087C3.83638 2.2186 3.96048 2.163 4.09738 2.163ZM4.23328 0.423302C5.28538 0.423302 6.23798 0.849802 6.92738 1.5392C7.61678 2.2286 8.04328 3.1813 8.04328 4.2333C8.04328 5.2854 7.61678 6.2381 6.92738 6.9274C6.23808 7.6168 5.28538 8.0433 4.23328 8.0433C3.83728 8.0433 3.45468 7.9824 3.09478 7.8698C2.74428 7.7602 2.41558 7.6012 2.11738 7.4018L0.650379 7.981C0.585179 8.0067 0.511379 7.9747 0.485679 7.9095C0.472579 7.8762 0.474479 7.8407 0.488179 7.8105L1.06478 6.3493C0.865179 6.0511 0.706379 5.7224 0.596679 5.3719C0.484079 5.012 0.423279 4.6294 0.423279 4.2333C0.423279 3.1813 0.849779 2.2286 1.53918 1.5392C2.22858 0.849802 3.18118 0.423302 4.23328 0.423302ZM6.74778 1.7188C6.10438 1.0754 5.21528 0.677302 4.23328 0.677302C3.25128 0.677302 2.36218 1.0754 1.71878 1.7188C1.07538 2.3622 0.677279 3.2514 0.677279 4.2333C0.677279 4.6042 0.733879 4.9615 0.838679 5.2965C0.946879 5.6421 1.10688 5.9658 1.30938 6.2576C1.33468 6.2916 1.34238 6.3376 1.32568 6.38L0.829079 7.6374L2.08658 7.1409C2.12478 7.126 2.16948 7.1297 2.20558 7.1548C2.49838 7.3583 2.82308 7.5193 3.17018 7.6278C3.50518 7.7326 3.86238 7.7893 4.23328 7.7893C5.21528 7.7893 6.10448 7.3912 6.74778 6.7478C7.39118 6.1045 7.78928 5.2153 7.78928 4.2333C7.78928 3.2514 7.39118 2.3622 6.74778 1.7188ZM2.49478 2.0088C2.43978 2.0518 2.36018 2.042 2.31718 1.987C2.27418 1.932 2.28398 1.8524 2.33898 1.8094L2.41838 1.7497L2.50088 1.6911C3.20278 1.2128 4.03228 1.0642 4.80558 1.2107C5.57928 1.3572 6.29718 1.799 6.77548 2.5009C7.12818 3.0185 7.30168 3.6062 7.30938 4.1897C7.31748 4.7908 7.14958 5.3874 6.82108 5.8984C6.78328 5.9573 6.70478 5.9744 6.64588 5.9366C6.58698 5.8988 6.56988 5.8203 6.60768 5.7614C6.90948 5.2919 7.06378 4.7442 7.05638 4.1927C7.04938 3.6581 6.88998 3.1191 6.56608 2.6437C6.12708 1.9995 5.46838 1.594 4.75898 1.4597C4.04918 1.3253 3.28778 1.4616 2.64368 1.9005L2.56798 1.9539L2.49478 2.0088ZM6.33148 6.1227C6.37828 6.0707 6.45858 6.0664 6.51058 6.1132C6.56258 6.16 6.56688 6.2403 6.52008 6.2923C6.43968 6.3814 6.35208 6.4676 6.25798 6.5497C6.16558 6.6302 6.06798 6.7058 5.96568 6.7755C5.90868 6.8132 5.85448 6.8474 5.79638 6.8831C5.73618 6.9187 5.65838 6.8987 5.62278 6.8385C5.58718 6.7783 5.60718 6.7005 5.66738 6.6649C5.72088 6.6324 5.77048 6.6004 5.82288 6.5661C5.91828 6.5011 6.00798 6.4318 6.09138 6.3591C6.17588 6.2854 6.25618 6.2062 6.33148 6.1227ZM5.26568 6.8613C5.33088 6.8359 5.40448 6.8681 5.42988 6.9333C5.45528 6.9985 5.42308 7.0721 5.35788 7.0975C4.69818 7.3573 3.97978 7.3745 3.32438 7.1723C2.68168 6.974 2.09898 6.5644 1.69108 5.9657C1.28288 5.3667 1.11478 4.6748 1.16518 4.0043C1.21668 3.3204 1.49578 2.6579 1.97918 2.1387C2.02678 2.0875 2.10708 2.0846 2.15828 2.1322C2.20948 2.1798 2.21238 2.2601 2.16478 2.3113C1.72138 2.7875 1.46538 3.3953 1.41818 4.0231C1.37188 4.6385 1.52598 5.2734 1.90048 5.8229C2.27498 6.3726 2.80958 6.7485 3.39878 6.9303C4.00028 7.1159 4.65978 7.0999 5.26568 6.8613ZM4.26868 2.4876C4.22508 2.4441 4.16438 2.417 4.09738 2.417C4.03058 2.417 3.96998 2.4442 3.92638 2.4878C3.88258 2.5313 3.85558 2.5919 3.85558 2.6589C3.85558 2.7231 3.88058 2.7816 3.92108 2.8249L3.92638 2.8299C3.96998 2.8735 4.03058 2.9007 4.09738 2.9007C4.16168 2.9007 4.22018 2.8757 4.26348 2.8352L4.26848 2.8299C4.31208 2.7863 4.33928 2.7257 4.33928 2.6589C4.33928 2.5919 4.31228 2.5313 4.26868 2.4876ZM3.61828 3.2931H4.18008C4.31308 3.2931 4.43368 3.3474 4.52098 3.4347C4.60828 3.522 4.66258 3.6428 4.66258 3.7756V5.5205H4.79378C4.90158 5.5205 4.99968 5.5647 5.07038 5.6354L5.07818 5.6439C5.14448 5.7142 5.18528 5.8088 5.18528 5.912C5.18528 6.0192 5.14128 6.1172 5.07038 6.1882C4.99868 6.2597 4.90098 6.3036 4.79378 6.3036H4.21638C4.08348 6.3036 3.96268 6.2494 3.87538 6.1621C3.78808 6.0748 3.73388 5.954 3.73388 5.8211V3.9671H3.61828C3.52558 3.9671 3.44118 3.9292 3.38018 3.8682C3.31918 3.8072 3.28128 3.7228 3.28128 3.6301C3.28128 3.5379 3.31918 3.4536 3.38018 3.3925C3.44198 3.3309 3.52618 3.2931 3.61828 3.2931ZM4.18008 3.5471H3.61828C3.59528 3.5471 3.57448 3.5564 3.55978 3.5711C3.54458 3.5863 3.53528 3.6071 3.53528 3.6301C3.53528 3.6527 3.54478 3.6736 3.55978 3.6886C3.57478 3.7036 3.59568 3.7131 3.61828 3.7131H3.86088C3.93098 3.7131 3.98788 3.77 3.98788 3.8401V5.8211C3.98788 5.8839 4.01368 5.9412 4.05498 5.9825C4.09628 6.0238 4.15358 6.0496 4.21638 6.0496H4.79378C4.83178 6.0496 4.86618 6.0343 4.89078 6.0096C4.91598 5.9845 4.93128 5.95 4.93128 5.912C4.93128 5.8765 4.91788 5.844 4.89588 5.8198L4.89078 5.815C4.86588 5.7901 4.83148 5.7745 4.79378 5.7745H4.53558C4.46548 5.7745 4.40858 5.7176 4.40858 5.6475V3.7756C4.40858 3.7129 4.38278 3.6557 4.34138 3.6143C4.29998 3.5729 4.24278 3.5471 4.18008 3.5471Z" fill="rgb(252, 234, 198)" />
                            </svg>
                            <p style={{
                                fontFamily: 'Playfair Display, serif',
                                fontSize: isMobile ? '1.1em' : '1.35em',
                                textAlign: 'left',
                            }}>{textWithBreaks}</p>
                            {/*<div style={{
                                marginTop: isMobile ? '0px' : '20px',
                                lineHeight: '1.5em',
                                fontSize: '0.95em',
                            }}>
                                <p>{property.description}</p>
                        </div>*/}
                        </div>
                        <Amenities incoming={incoming.amenities} />
                    </section>
                    <section style={{
                        marginTop: '20px',
                        marginLeft: isMobile ? '10px' : '40px',
                        marginRight: isMobile ? '10px' : '40px',
                    }}>
                        <MediaExplorer videos={incoming?.externalVideos} images={incoming?.photos} />
                    </section>
                    {(incoming?.spaceGroups && incoming?.spaceGroups.length > 0) && (
                        <section style={{
                            marginTop: '100px',
                            marginLeft: isMobile ? '10px' : '40px',
                            marginRight: isMobile ? '10px' : '40px',
                        }}>
                            <h1 style={{
                                fontSize: isMobile ? '2em' : '3em',
                                marginBottom: '30px',
                                fontFamily: 'Anuphan',
                            }}>Spaces</h1>
                            <TabBar spaceGroups={incoming?.spaceGroups} />
                        </section>)}
                    {/*<PropertyDetailSection property={property} breakpoint={breakpoint} />*/}
                    <Footer breakpoint={breakpoint} />
                </div>
            </>
        );
    }
};

export default PropertyView;
